import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Sparkles } from "lucide-react";
import { KIKI } from "@/constants/testIds";
import { useT, useLang } from "@/lib/i18n";

const PARTNERS = [
  { name: "MARUTI SUZUKI", originKey: "japan" },
  { name: "MIKUNI", originKey: "japan" },
  { name: "SANKO", originKey: "japan" },
  { name: "NAGATA INDIA", originKey: "japan" },
  { name: "KOIDE", originKey: "japan" },
  { name: "HI-LEX", originKey: "japan" },
  { name: "IFB", originKey: "indianMnc" },
  { name: "MOTHERSON", originKey: "indianMnc" },
  { name: "SPINKS", originKey: "indianMnc" },
  { name: "SANDHAR", originKey: "indianMnc" },
  { name: "SUBROS", originKey: "indianCo" },
  { name: "RICO", originKey: "indianCo" },
  { name: "ATMA", originKey: "indianCo" },
  { name: "ROOP POLYMERS", originKey: "indianCo" },
  { name: "STRYKER", originKey: "american" },
  { name: "BOSTON SCIENTIFIC", originKey: "american" },
  { name: "BUCHER HYDRAULICS", originKey: "german" },
  { name: "+ MANY MORE", originKey: "more" },
];

export function PlacementBlock({ onEnquire }) {
  const t = useT();
  const { lang } = useLang();
  const isHi = lang === "hi";
  return (
    <section id="outcomes" data-testid={KIKI.outcomes} className="relative py-16 md:py-24">
      <div className="max-w-[90rem] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {/* Left: salary card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 md:col-span-4 brochure-card bg-card border border-border p-8 md:p-10 flex flex-col justify-between"
          >
            <div>
              <div className={`label-overline text-muted-foreground ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("placement.salaryLabel")}</div>
              <div className="mt-6">
                <span className="font-sans font-black text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight break-keep whitespace-nowrap">₹1.5&ndash;4.8L</span>
                <span className={`block label-overline text-muted-foreground mt-3 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("placement.perYear")}</span>
              </div>
              <p className={`mt-6 text-sm text-muted-foreground max-w-xs leading-relaxed ${isHi ? "font-hindi" : ""}`}>
                {t("placement.salaryNote")}
              </p>
            </div>
            <button onClick={onEnquire} className="mt-8 inline-flex items-center gap-3 bg-[#FFCC00] text-black rounded-full px-5 py-3 self-start group">
              <ShieldCheck className="w-4 h-4" strokeWidth={2} />
              <span className={`text-sm font-semibold ${isHi ? "font-hindi" : ""}`}>{t("placement.certPill")}</span>
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
            </button>
          </motion.div>

          {/* Right: partners */}
          <div className="col-span-12 md:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 sm:gap-4 mb-6"
            >
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="font-mono text-sm text-muted-foreground">04</span>
                <span className={`label-overline text-muted-foreground ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("placement.overline")}</span>
              </div>
              <span className={`text-lg md:text-2xl font-bold ${isHi ? "font-hindi" : "font-sans"}`}>{t("placement.title")}</span>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {PARTNERS.map((p, i) => (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, scale: 0.96 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.02, ease: [0.16, 1, 0.3, 1] }}
                  className="brochure-card !p-4 bg-card border border-border card-hover flex items-center gap-3"
                >
                  <div className="w-9 h-9 grid place-items-center rounded-lg bg-[#FFCC00]/20 text-[#0A0A0A] shrink-0">
                    <Sparkles className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-[12.5px] leading-tight truncate">{p.name}</div>
                    <div className={`label-overline text-muted-foreground text-[9px] mt-1 ${isHi ? "font-hindi tracking-normal text-[10.5px]" : ""}`}>{t(`placement.origins.${p.originKey}`)}</div>
                  </div>
                </motion.div>
              ))}
            </div>
            <p className={`mt-5 text-xs text-muted-foreground max-w-3xl leading-relaxed ${isHi ? "font-hindi" : ""}`}>
              {t("placement.disclaimer")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
